import React, { useEffect, useMemo, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/groupDetailsDialogStyle';
import CustomInput from 'components/CustomInput/CustomInput';
import Button from 'components/CustomButtons/Button';
import CustomTable from 'components/CustomTable/CustomTable';
import { PhoneNumberCell } from 'components/Cells/Cells';
import TableHeader from 'components/TableHeader/TableHeader';
import CustomPopUpWithLoader from 'components/CustomPopUpWithLoader/CustomPopUpWithLoader';
import Add from '@material-ui/icons/Add';
import AddNumbersDialog from 'views/PrepaidLoyalty/commons/tabs/AddNumbersTab/subcomponents/AddNumbersDialog/index';
import { BodyS } from 'components/Typography/Typography';
import { LoyaltyGroup } from 'utils/api/models/prepaidLoyalty/loyaltyGroup';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { finalize } from 'rxjs/operators';
import { LoyaltyGroupDetails } from 'utils/api/models/prepaidLoyalty/loyaltyGroupDetails';
import { SingleLoyaltyNumber } from 'utils/api/models/prepaidLoyalty/singleLoyaltyNumber';
import invalidateCache from 'utils/api/invalidateCache';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import CanPerform from 'components/CanPerform/CanPerform';
import _ from 'lodash';
import Attention from 'assets/img/icons/attention.svg';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IGroupDetailsDialog {
	isOpen: boolean;
	hideDialog: () => void;
	refreshGroups: () => void;
	group?: LoyaltyGroup | undefined;
	setCurrentGroup: React.Dispatch<React.SetStateAction<LoyaltyGroup | undefined>>;
}

const GroupDetailsDialog = ({ isOpen, hideDialog, group, refreshGroups, setCurrentGroup }: IGroupDetailsDialog) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [currentGroupDetails, setCurrentGroupDetails] = useState<LoyaltyGroupDetails>();
	const [currentGroupUnsaved, setCurrentGroupUnsaved] = useState(false);
	const [inputGroupNameValue, setInputGroupNameValue] = useState('');
	const [selected, setSelected] = useState<number[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [popUpGroupSavedOpen, setPopUpGroupSavedOpen] = useState<boolean>(false);
	const [popUpGroupSavedState, setPopUpGroupSavedState] = useState<boolean>(false);
	const [popUpDiscardUnsavedGroupOpen, setPopUpDiscardUnsavedGroupOpen] = useState<boolean>(false);
	const [addNewNumberDialogState, setAddNewNumberDialogState] = useState<boolean>(false);
	const classes = useStyles();

	useEffect(() => {
		setSelected([]);
	}, []);

	useEffect(() => {
		setCurrentGroupUnsaved(false);
		setCurrentGroupDetails(undefined);
		if (group) {
			setLoading(true);
			LoyaltyGroupService.getLoyaltyGroupDetails(group.id)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe((loyaltyGroupDetails) => {
					setCurrentGroupDetails(loyaltyGroupDetails);
					setInputGroupNameValue(loyaltyGroupDetails.name);
				});
		}
	}, [group]);

	const onSave = () => {
		if (currentGroupDetails) {
			setLoading(true);
			LoyaltyGroupService.updateLoyaltyGroup(currentGroupDetails.id, {
				name: currentGroupDetails?.name,
				numbers: currentGroupDetails?.numbers.map((num) => num.number),
			})
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe((createLoyaltyResponse) => {
					hideDialog();
					setLoading(false);
					setPopUpGroupSavedState(true);
					setPopUpGroupSavedOpen(true);
					invalidateCache(['getLoyaltyGroups'], true);
					invalidateCache(['getLoyaltyGroupDetails'], true);
					refreshGroups();
				});
		}
	};

	const setGroupName = (newName: string): void => {
		setCurrentGroupDetails((oldGroup) => {
			return { ...oldGroup, name: newName } as LoyaltyGroupDetails;
		});
		setInputGroupNameValue(newName);
		setCurrentGroupUnsaved(true);
	};

	const groupDetailsHideDialog = () => {
		setCurrentGroup(undefined);
		hideDialog();
	};

	const addNewNumberButtonClicked = () => {
		setAddNewNumberDialogState(true);
	};

	const hideAddNewNumberDialog = () => {
		setAddNewNumberDialogState(false);
	};

	const numbersWithEmptyId = (numbers: string[]): SingleLoyaltyNumber[] => {
		return numbers.map((number) => {
			return {
				id: '',
				number,
			} as SingleLoyaltyNumber;
		});
	};

	const addNumbers = (numbers: string[]) => {
		setCurrentGroupDetails((oldGroup) => {
			return {
				...oldGroup,
				numbers: _.uniqBy([...(oldGroup?.numbers || []), ...numbersWithEmptyId(numbers)], 'number'),
			} as LoyaltyGroupDetails;
		});
		setCurrentGroupUnsaved(true);
		setSelected([]);
	};

	const deleteNumbers = () => {
		setSelected([]);
		setCurrentGroupDetails((oldGroup) => {
			return {
				...oldGroup,
				numbers: _.uniqBy(
					(oldGroup?.numbers || []).filter((_, index) => selected.indexOf(index) === -1),
					'number'
				),
			} as LoyaltyGroupDetails;
		});
		setCurrentGroupUnsaved(true);
	};

	const hideGroupSavedDialog = () => {
		hideDialog();
		setPopUpGroupSavedOpen(false);
		setCurrentGroup(undefined);
		refreshGroups();
	};

	const discardGroupChanges = () => {
		if (currentGroupUnsaved) {
			setPopUpDiscardUnsavedGroupOpen(true);
		} else {
			hideGroupSavedDialog();
		}
	};

	const filteredNumbers: string[] = useMemo(() => {
		return (currentGroupDetails?.numbers || [])
			.map((num) => num.number)
			.filter((num) => num.includes(searchValue.toLowerCase()));
	}, [currentGroupDetails, searchValue]);

	return (
		<CustomDialog
			header={translate('prepaid.loyalty.manage.groups.details')}
			description={<BodyS color="secondary-light">{currentGroupDetails?.name || ''}</BodyS>}
			isOpen={isOpen}
			hideDialog={discardGroupChanges}
			loading={loading}
			width={668}
			dialogBody={
				<>
					<ConditionalRender
						show={!!group?.subscriptionsCount}
						onTrue={() => (
							<div className={classes.neutralBackground}>
								<div className={classes.subscriptionNoteWrapper}>
									<img src={Attention} alt="Note icon" className={classes.image} />
									<BodyS color="brand-secondary" disableMargin>
										{translate('prepaid.loyalty.manage.groups.ongoing.subscription.note')}
									</BodyS>
								</div>
							</div>
						)}
					/>
					<div className={classes.dialogWrapper}>
						<CanPerform
							action="prepaid-loyalty:group-details-change-group-name"
							yes={
								<CustomInput
									labelText={translate('prepaid.loyalty.manage.groups.change.name')}
									inputProps={{
										required: true,
										value: inputGroupNameValue,
										type: 'text',
										onChange: (event: React.FormEvent<HTMLInputElement>) =>
											setGroupName((event.target as HTMLInputElement).value),
									}}
									formControlProps={{
										fullWidth: true,
									}}
								/>
							}
						/>
					</div>
					<div className={classes.tableWrapper}>
						<TableHeader
							searchInputValue={searchValue}
							setSearchInputValue={(value) => setSearchValue(value)}
							buttons={{
								text: (
									<>
										<Add />
										{translate('prepaid.loyalty.add.numbers')}
									</>
								),
								onClick: addNewNumberButtonClicked,
								action: 'prepaid-loyalty:group-details-add-numbers',
							}}
							stroke={true}
						/>

						<CustomTable
							withCheckbox
							withHeader
							selected={selected}
							onSelect={setSelected}
							total={(filteredNumbers || [])?.length}
							tableProps={{
								data: filteredNumbers || [],
								columns: [
									{
										accessor: '',
										Header: translate('prepaid.loyalty.manage.groups.details.table.phone.number'),
										Cell: ({ original }: { original: string }) => (
											<PhoneNumberCell text={original} />
										),
										sortable: true,
									},
								],
								defaultPageSize: 5,
								stroke: true,
							}}
							selectPanel={{
								selectedElements: selected.length,
								deleteItemsAction: 'prepaid-loyalty:group-details-delete-numbers',
								deleteItems: !group?.subscriptionsCount ? deleteNumbers : undefined,
								confirmationNotRequired: true,
							}}
						/>
					</div>
					<CanPerform
						action="prepaid-loyalty:group-details-save-cancel-buttons"
						yes={
							<div className={classes.buttonsWrapper}>
								<Button color="primary-light" onClick={onSave} disabled={!currentGroupUnsaved}>
									{translate('save')}
								</Button>
							</div>
						}
					/>
					<CustomPopUpWithLoader
						isOpen={popUpGroupSavedOpen}
						hideDialog={hideGroupSavedDialog}
						loading={loading}
						isError={!popUpGroupSavedState}
						header={[]}
						noMargin={true}
						body={translate(
							`prepaid.loyalty.manage.groups.details.edit.${popUpGroupSavedState ? 'success' : 'error'}`
						)}
					/>
					<ConfirmationDialog
						isOpen={popUpDiscardUnsavedGroupOpen}
						hideDialog={() => setPopUpDiscardUnsavedGroupOpen(false)}
						header={translate('prepaid.loyalty.manage.groups.details.discard.changes')}
						buttons={[
							{
								text: translate('cancel'),
								onClick: () => setPopUpDiscardUnsavedGroupOpen(false),
								color: 'tertiary-light',
							},
							{
								text: translate('discard'),
								onClick: () => {
									setPopUpDiscardUnsavedGroupOpen(false);
									groupDetailsHideDialog();
								},
								color: 'primary-light',
							},
						]}
					/>
					<AddNumbersDialog
						isOpen={addNewNumberDialogState}
						hideDialog={hideAddNewNumberDialog}
						addNumbers={addNumbers}
					/>
				</>
			}
		/>
	);
};

export default GroupDetailsDialog;
